import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { loxone, type KontrolaImportu, type Miniserver } from '../api/client.js';
import { useApi } from '../api/use-api.js';
import { EmptyState } from '../components/states.js';
import {
  Badge,
  Button,
  Card,
  DataRow,
  Field,
  Notice,
  PageHeader,
  SectionTitle,
} from '../components/ui.js';
import {
  IconAlert,
  IconCheck,
  IconChevronRight,
  IconPlus,
  IconRefresh,
  IconServer,
  IconTrash,
} from '../components/icons.js';
import { useSession } from '../auth/session.js';
import { Data } from './common.js';

/*
 * Miniservery.
 *
 * Parovanie najprv overi spojenie a az potom ulozi, takze formular je
 * zaroven testom udajov - preto je chybova hlaska hned pod nim a nie
 * niekde v rohu.
 *
 * Kontrola po importe je tu vedla kazdeho Miniservera zamerne: je to
 * prva vec, ktorou sa riesi "nechodia mi stavy".
 */

function FormularParovania({
  poSparovani,
  zavri,
}: {
  readonly poSparovani: () => void;
  readonly zavri: () => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [host, setHost] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [configVersion, setConfigVersion] = useState('');
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);

  const odosli = (event: FormEvent) => {
    event.preventDefault();
    setChyba(undefined);
    setPrebieha(true);
    void loxone
      .pair({
        name,
        host,
        username,
        password,
        ...(configVersion.trim() === '' ? {} : { configVersion: configVersion.trim() }),
      })
      .then(() => {
        poSparovani();
        zavri();
      })
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Spárovanie zlyhalo.');
      })
      .finally(() => {
        setPrebieha(false);
      });
  };

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Nový Miniserver</SectionTitle>
      <p className="text-sm text-(--color-text-muted)">
        LoxLink sa najprv pripojí a načíta štruktúrny súbor. Ak sú údaje zlé alebo je Miniserver
        nedostupný, neuloží sa nič.
      </p>

      <form onSubmit={odosli} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Názov" value={name} onChange={setName} required placeholder="Dom" />
          <Field
            label="Adresa"
            value={host}
            onChange={setHost}
            required
            placeholder="192.168.1.20"
            hint="IP alebo názov v sieti, bez http://"
          />
          <Field
            label="Prihlasovacie meno"
            value={username}
            onChange={setUsername}
            required
            autoComplete="off"
          />
          <Field
            label="Heslo"
            type="password"
            value={password}
            onChange={setPassword}
            required
            autoComplete="new-password"
          />
          <Field
            label="Verzia Loxone Config"
            value={configVersion}
            onChange={setConfigVersion}
            placeholder="17.1.7.27"
            hint="Nájdeš ju v Loxone Config: Pomocník → Informácie. Zapíše sa do šablón."
          />
        </div>

        {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={prebieha}>
            {prebieha ? 'Overujem spojenie…' : 'Spárovať'}
          </Button>
          <Button variant="ghost" onClick={zavri} disabled={prebieha}>
            Zrušiť
          </Button>
        </div>
      </form>
    </Card>
  );
}

function VysledokKontroly({ vysledok }: { readonly vysledok: KontrolaImportu }): ReactNode {
  if (!vysledok.checked) {
    return (
      <Notice tone="warn">
        Miniserver nebol dostupný, takže sa nedá povedať nič. Skús to znova, keď bude online.
      </Notice>
    );
  }
  if (vysledok.ok) {
    return (
      <Notice tone="ok">
        Všetkých {vysledok.expected.length} objektov je v Miniserveri. Stavy môžu chodiť.
      </Notice>
    );
  }
  return (
    <Notice tone="danger">
      <p className="font-medium">V Miniserveri chýba {vysledok.missing.length} objektov.</p>
      <p className="mt-1">
        Skoro vždy to znamená neimportovanú šablónu alebo nenahratý program do Miniservera.
      </p>
      <ul className="mt-2 flex flex-col gap-0.5 font-mono text-xs">
        {vysledok.missing.map((m) => (
          <li key={m}>{m}</li>
        ))}
      </ul>
    </Notice>
  );
}

function KartaMiniservera({
  miniserver,
  poZmene,
}: {
  readonly miniserver: Miniserver;
  readonly poZmene: () => void;
}): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [kontrola, setKontrola] = useState<KontrolaImportu | undefined>(undefined);
  const [chyba, setChyba] = useState<string | undefined>(undefined);
  const [prebieha, setPrebieha] = useState(false);
  const [rusi, setRusi] = useState(false);

  const skontroluj = () => {
    setChyba(undefined);
    setPrebieha(true);
    void loxone
      .check(miniserver.id)
      .then(({ check }) => setKontrola(check))
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Kontrola zlyhala.');
      })
      .finally(() => setPrebieha(false));
  };

  const zrus = () => {
    setChyba(undefined);
    void loxone
      .unpair(miniserver.id)
      .then(poZmene)
      .catch((error: unknown) => {
        setChyba(error instanceof Error ? error.message : 'Zrušenie párovania zlyhalo.');
      });
  };

  return (
    <Card as="li" className="flex flex-col gap-4 p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="text-(--color-text-subtle)">
            <IconServer />
          </span>
          <div className="flex min-w-0 flex-col">
            <span className="truncate font-medium text-(--color-text)">{miniserver.name}</span>
            <span className="truncate text-xs text-(--color-text-subtle)">{miniserver.host}</span>
          </div>
        </div>
        <Badge tone={miniserver.connected ? 'ok' : 'danger'}>
          {miniserver.connected ? 'Pripojený' : 'Nedostupný'}
        </Badge>
      </div>

      {!miniserver.connected && miniserver.lastError !== undefined && (
        <p className="text-sm text-(--color-text-muted)">{miniserver.lastError}</p>
      )}

      <dl className="border-y border-(--color-border) py-1">
        <DataRow label="Sériové číslo">
          <span className="font-mono text-xs">{miniserver.serialNumber || 'zatiaľ neznáme'}</span>
        </DataRow>
        <DataRow label="Verzia Loxone Config">{miniserver.configVersion ?? 'nezadaná'}</DataRow>
        <DataRow label="Priradené zariadenia">{miniserver.deviceCount}</DataRow>
      </dl>

      {chyba !== undefined && <Notice tone="danger">{chyba}</Notice>}
      {kontrola && <VysledokKontroly vysledok={kontrola} />}

      {jeSpravca && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={skontroluj} disabled={prebieha} icon={<IconCheck className="size-4" />}>
            {prebieha ? 'Kontrolujem…' : 'Skontrolovať import'}
          </Button>
          {rusi ? (
            <>
              <Button variant="danger" onClick={zrus}>
                Naozaj zrušiť párovanie
              </Button>
              <Button variant="ghost" onClick={() => setRusi(false)}>
                Nechať
              </Button>
            </>
          ) : (
            <Button
              variant="danger"
              icon={<IconTrash className="size-4" />}
              onClick={() => setRusi(true)}
            >
              Zrušiť párovanie
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

/*
 * Sprievodca zapojenim do Loxone.
 *
 * Loxone nema API na vytvaranie objektov, takze cast postupu robi
 * clovek rucne v Loxone Config. Sprievodca preto NEPREDSTIERA, ze vie
 * vsetko: kroky, ktorych stav LoxLink pozna, su odskrtnute podla dat,
 * a rucne kroky su ako rucne aj oznacene. Tvarit sa, ze vieme, ci
 * niekto nahral program do Miniservera, by bola lož.
 */

type StavKroku = 'hotovo' | 'caka' | 'rucne' | 'nezname';

interface Krok {
  readonly title: string;
  readonly stav: StavKroku;
  readonly detail: ReactNode;
}

const POPIS_KROKU: Record<StavKroku, string> = {
  hotovo: 'Hotové',
  caka: 'Čaká',
  rucne: 'Ručný krok',
  nezname: 'Neznáme',
};

function IkonaKroku({ stav }: { readonly stav: StavKroku }): ReactNode {
  if (stav === 'hotovo') {
    return (
      <span className="text-(--color-ok)">
        <IconCheck className="size-4" />
      </span>
    );
  }
  if (stav === 'caka') {
    return (
      <span className="text-(--color-warn)">
        <IconAlert className="size-4" />
      </span>
    );
  }
  return (
    <span className="text-(--color-text-subtle)">
      <IconChevronRight className="size-4" />
    </span>
  );
}

function SprievodcaImportom({
  miniservery,
}: {
  readonly miniservery: readonly Miniserver[];
}): ReactNode {
  const mapovania = useApi((signal) => loxone.mappings(signal));
  const pocetMapovani = mapovania.data?.mappings.length;

  const kroky: readonly Krok[] = [
    {
      title: 'Spárovať Miniserver',
      stav: miniservery.length > 0 ? 'hotovo' : 'caka',
      detail:
        miniservery.length > 0
          ? `Spárované: ${miniservery.map((m) => m.name).join(', ')}.`
          : 'LoxLink si od Miniservera vypýta sériové číslo a overí prihlasovacie údaje.',
    },
    {
      title: 'Priradiť zariadenie',
      // Kym pocet mapovani nepoznam, krok sa netvari ani za hotovy,
      // ani za nesplneny - to by bola vymyslena informacia.
      stav: pocetMapovani === undefined ? 'nezname' : pocetMapovani > 0 ? 'hotovo' : 'caka',
      detail: (
        <>
          {mapovania.loading
            ? 'Načítavam mapovania…'
            : mapovania.error !== undefined
              ? 'Zoznam mapovaní sa nepodarilo načítať.'
              : pocetMapovani !== undefined && pocetMapovani > 0
                ? `Priradených mapovaní: ${pocetMapovani}.`
                : 'Bez mapovania nemá LoxLink kam stavy posielať.'}{' '}
          <Link to="/mapovania" className="font-medium text-(--color-accent)">
            Otvoriť Mapovania
          </Link>
        </>
      ),
    },
    {
      title: 'Stiahnuť šablóny',
      stav: 'rucne',
      detail:
        'Ku každému mapovaniu patria dva súbory XML — virtuálny vstup a virtuálny výstup. Sťahujú sa v Mapovaniach.',
    },
    {
      title: 'Naimportovať v Loxone Config a nahrať program',
      stav: 'rucne',
      detail:
        'Loxone nemá API na vytváranie objektov, takže tento krok spraví človek. Bez nahratia programu do Miniservera objekty nevzniknú.',
    },
    {
      title: 'Skontrolovať import',
      stav: 'rucne',
      detail:
        'Tlačidlom pri Miniserveri nižšie. LoxLink si vypýta štruktúru a povie, ktoré objekty v nej naozaj sú.',
    },
  ];

  return (
    <Card className="flex flex-col gap-4 p-4 sm:p-5">
      <SectionTitle>Ako zapojiť zariadenie do Loxone</SectionTitle>
      <ol className="flex flex-col gap-3">
        {kroky.map((krok, index) => (
          <li key={krok.title} className="flex gap-3">
            <span className="mt-0.5 shrink-0">
              <IkonaKroku stav={krok.stav} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-(--color-text)">
                  {index + 1}. {krok.title}
                </span>
                <Badge tone={krok.stav === 'hotovo' ? 'ok' : 'neutral'}>
                  {POPIS_KROKU[krok.stav]}
                </Badge>
              </div>
              <p className="text-sm text-(--color-text-muted)">{krok.detail}</p>
            </div>
          </li>
        ))}
      </ol>
    </Card>
  );
}

export function Loxone(): ReactNode {
  const { user } = useSession();
  const jeSpravca = user?.role === 'admin';
  const [paruje, setParuje] = useState(false);
  const nacitanie = useApi((signal) => loxone.miniservers(signal));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Loxone"
        detail="Spárované Miniservery a kontrola, či sa šablóny naozaj naimportovali."
        action={
          jeSpravca ? (
            <Button
              variant="primary"
              icon={<IconPlus className="size-4" />}
              onClick={() => setParuje(true)}
              disabled={paruje}
            >
              Spárovať Miniserver
            </Button>
          ) : (
            <Button
              onClick={nacitanie.reload}
              icon={<IconRefresh className="size-4" />}
              variant="ghost"
            >
              Obnoviť
            </Button>
          )
        }
      />

      {paruje && (
        <FormularParovania poSparovani={nacitanie.reload} zavri={() => setParuje(false)} />
      )}

      <SprievodcaImportom miniservery={nacitanie.data?.miniservers ?? []} />

      <Data nacitanie={nacitanie}>
        {(data) =>
          data.miniservers.length === 0 ? (
            <EmptyState
              title="Žiadny spárovaný Miniserver"
              detail={
                jeSpravca
                  ? 'Spáruj prvý Miniserver. LoxLink si od neho vypýta sériové číslo a overí prihlasovacie údaje.'
                  : 'Párovanie môže urobiť len správca.'
              }
            />
          ) : (
            <ul className="flex flex-col gap-3">
              {data.miniservers.map((m) => (
                <KartaMiniservera key={m.id} miniserver={m} poZmene={nacitanie.reload} />
              ))}
            </ul>
          )
        }
      </Data>
    </div>
  );
}
